/* Helpers for reading the public match feed. */

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

/**
 * Whether a match has finished.
 *
 * `match_status` is only stamped by /match-status; the list feeds omit it
 * entirely (0 of 58 completed records carry it). Treating "absent" as "not
 * completed" would silently render a finished match as LIVE with no score, so
 * fall back to recorded results, then to the clock.
 */
export function isCompletedMatch(match) {
  if (!match) return false;
  if (match.match_status) return match.match_status === 'completed';

  // `win_result` marks a DECIDED match: 58/58 completed records carry it,
  // 0/3 upcoming do. Checking merely for the presence of result entries would
  // be wrong — a BO7 in progress has 1-2 entries and would be reported as
  // finished, presenting a partial score as final.
  const decided = (match.teams || []).some((team) => team.win_result === 1);
  if (decided) return true;

  const start = matchStart(match);
  return Boolean(start) && Date.now() - start.getTime() > SIX_HOURS_MS;
}

/** Combine the feed's separate date + time fields into a Date. */
export function matchStart(match) {
  if (!match?.date) return null;
  const started = new Date(`${match.date}T${match.time || '00:00:00'}`);
  return Number.isNaN(started.getTime()) ? null : started;
}

/** Short, human countdown: "in 2d 4h", "in 35m", or "Live now" once it starts. */
export function countdownTo(target, now = new Date()) {
  if (!target) return null;
  const totalMinutes = Math.floor((target - now) / 60000);
  if (totalMinutes <= 0) return 'Live now';

  const days    = Math.floor(totalMinutes / 1440);
  const hours   = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0)  return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

/**
 * The feed is inconsistent about stream_links — sometimes an array, sometimes
 * an object keyed by platform, sometimes bare strings. Pull the first usable
 * URL out of whatever shape arrives.
 */
export function firstStreamUrl(links) {
  if (!links) return null;
  const candidates = Array.isArray(links) ? links : Object.values(links);

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.startsWith('http')) {
      return candidate;
    }
    if (candidate && typeof candidate === 'object') {
      const url = candidate.url || candidate.link || candidate.href;
      if (typeof url === 'string' && url.startsWith('http')) return url;
    }
  }
  return null;
}
